import { roomCodeLabel, roomLabel } from './format';

/* --------------------------------------------------------------------- */
/* Series de evolución                                                    */
/*                                                                        */
/* Cada sensor publica en instantes propios, así que agrupar por          */
/* timestamp exacto deja una matriz dispersa: cada fila con un solo valor */
/* y el resto en null. Recharts no puede unir puntos separados por nulos  */
/* y el gráfico queda en blanco aunque la leyenda liste las series.       */
/* Por eso promediamos en cubos de tiempo lo bastante anchos como para    */
/* cubrir la cadencia real de los sensores: todas las series comparten el */
/* eje X y las interrupciones reales siguen viéndose como cortes.         */
/* --------------------------------------------------------------------- */

export const COLORS = [
  '#06b6d4', '#8b5cf6', '#10b981', '#f97316',
  '#ef4444', '#3b82f6', '#ec4899', '#eab308',
];

export const MAX_CHART_BUCKETS = 240;

export const BUCKET_STEPS = [
  60000, 120000, 300000, 600000, 900000, 1800000,
  3600000, 7200000, 10800000, 21600000, 43200000, 86400000,
];

export function niceBucket(ms) {
  return BUCKET_STEPS.find((step) => step >= ms) || BUCKET_STEPS[BUCKET_STEPS.length - 1];
}

const rowSeries = (row) => `${row.sala}#${row.sensor_num === null || row.sensor_num === undefined ? '' : row.sensor_num}`;

export function limitEvolutionSeries(rows, metric, preferredSeries, limit) {
  const present = new Set(rows
    .filter((row) => row[metric] !== null && row[metric] !== undefined)
    .map(rowSeries));
  if (present.size <= limit) return { rows, total: present.size, hidden: 0 };

  const allowed = new Set(preferredSeries.filter((serie) => present.has(serie)).slice(0, limit));
  for (const serie of present) {
    if (allowed.size >= limit) break;
    allowed.add(serie);
  }
  return {
    rows: rows.filter((row) => allowed.has(rowSeries(row))),
    total: present.size,
    hidden: present.size - allowed.size,
  };
}

// Etiqueta por defecto cuando no se pasa un resolutor de nombres: la unidad y
// el sensor. `/series` no conoce el catálogo, así que el nombre real lo inyecta
// la página, que sí lo tiene.
function etiquetaPorDefecto(sala, sensor) {
  return {
    label: `${roomLabel(sala)} · Sensor ${sensor}`,
    shortLabel: `Sala ${roomCodeLabel(String(sala).split('-')[1])} · S${sensor}`,
  };
}

export function buildEvolution(rows, metric, nameFor = null) {
  const groups = new Map();
  rows.forEach((row) => {
    const raw = row[metric];
    if (raw === null || raw === undefined) return;
    const time = new Date(row.telemetry_time).getTime();
    if (!Number.isFinite(time)) return;
    const id = `${row.sala}::${row.sensor_num}`;
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id).push({ time, value: Number(raw) });
  });
  if (!groups.size) return { data: [], meta: [], bucket: 0 };

  const series = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  series.forEach(([, points]) => points.sort((a, b) => a.time - b.time));

  // El cubo debe ser al menos tan ancho como la cadencia habitual (p90) para
  // que un sensor normal ocupe cubos contiguos y su línea quede continua.
  const gaps = [];
  let first = Infinity;
  let last = -Infinity;
  series.forEach(([, points]) => {
    first = Math.min(first, points[0].time);
    last = Math.max(last, points[points.length - 1].time);
    for (let index = 1; index < points.length; index += 1) {
      const gap = points[index].time - points[index - 1].time;
      if (gap > 0) gaps.push(gap);
    }
  });
  gaps.sort((a, b) => a - b);
  const cadence = gaps.length ? gaps[Math.min(gaps.length - 1, Math.floor(gaps.length * 0.9))] : 60000;
  const span = Math.max(0, last - first);
  const bucket = niceBucket(Math.max(cadence, Math.ceil(span / MAX_CHART_BUCKETS) || 60000));

  const meta = series.map(([id], index) => {
    const [sala, sensor] = id.split('::');
    const nombre = nameFor ? nameFor(sala, sensor) : null;
    const porDefecto = etiquetaPorDefecto(sala, sensor);
    return {
      id,
      key: `s${index}`,
      label: nombre || porDefecto.label,
      shortLabel: nombre || porDefecto.shortLabel,
      color: COLORS[index % COLORS.length],
    };
  });

  const slots = new Map();
  series.forEach(([, points], index) => {
    const key = `s${index}`;
    const accumulator = new Map();
    points.forEach((point) => {
      const slot = Math.floor(point.time / bucket) * bucket;
      const current = accumulator.get(slot) || { sum: 0, count: 0 };
      current.sum += point.value;
      current.count += 1;
      accumulator.set(slot, current);
    });
    accumulator.forEach((entry, slot) => {
      const row = slots.get(slot) || { timestamp: slot };
      row[key] = entry.sum / entry.count;
      slots.set(slot, row);
    });
  });

  const data = [...slots.values()].sort((a, b) => a.timestamp - b.timestamp);
  return { data, meta, bucket };
}
